
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Tool, ColorHex, Point, ExportFormat, PMode } from './types';
import { 
  WIDTH_PMODE1, HEIGHT_PMODE1, WIDTH_PMODE4, HEIGHT_PMODE4,
  PALETTES_PMODE1, PALETTES_PMODE4,
  PALETTE_NAMES_SET_PMODE1, PALETTE_NAMES_SET_PMODE4,
  ZOOM_PMODE1, ZOOM_PMODE4 
} from './constants';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import CanvasEditor from './components/CanvasEditor';
import PermutationView from './components/PermutationView';

interface RawImport {
  imageData: ImageData;
  bgIndex: number;
}

interface Scrap {
  data: Uint8Array;
  w: number;
  h: number;
}

const App: React.FC = () => {
  const [currentPMode, setCurrentPMode] = useState<PMode>(PMode.PMODE1);
  const [currentTool, setCurrentTool] = useState<Tool>(Tool.Pencil);
  const [currentColorIdx, setCurrentColorIdx] = useState<number>(3);
  const [paletteIdx, setPaletteIdx] = useState<number>(0);

  const WIDTH = currentPMode === PMode.PMODE1 ? WIDTH_PMODE1 : WIDTH_PMODE4;
  const HEIGHT = currentPMode === PMode.PMODE1 ? HEIGHT_PMODE1 : HEIGHT_PMODE4;
  const ZOOM = currentPMode === PMode.PMODE1 ? ZOOM_PMODE1 : ZOOM_PMODE4;

  const [pixelData, setPixelData] = useState<Uint8Array>(new Uint8Array(WIDTH_PMODE1 * HEIGHT_PMODE1).fill(0));
  const [history, setHistory] = useState<Uint8Array[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isPermutationMode, setIsPermutationMode] = useState(false);
  const [fontSizeIdx, setFontSizeIdx] = useState(2); // Default to 5x7
  const [scrap, setScrap] = useState<Scrap | null>(null);
  
  const [rawImport, setRawImport] = useState<RawImport | null>(null);
  const [importWeights, setImportWeights] = useState<number[]>([0, 0, 0, 0]);
  const [exportFormat, setExportFormat] = useState<ExportFormat>(ExportFormat.PNG);

  const currentPalette = useMemo(() => {
    return currentPMode === PMode.PMODE1 ? PALETTES_PMODE1[paletteIdx] : PALETTES_PMODE4[paletteIdx];
  }, [paletteIdx, currentPMode]);
  
  const paletteRGB = useMemo(() => {
    return currentPalette.map(hex => ({
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16)
    }));
  }, [currentPalette]);

  useEffect(() => {
    const initialData = new Uint8Array(WIDTH * HEIGHT).fill(0);
    setPixelData(initialData);
    setHistory([initialData]);
    setHistoryIndex(0);
    // Reset color index if it's out of bounds for the new mode
    if (currentPMode === PMode.PMODE4 && currentColorIdx > 1) {
      setCurrentColorIdx(1);
    } else if (currentPMode === PMode.PMODE1 && currentColorIdx === 0) {
      setCurrentColorIdx(3);
    }
  }, [currentPMode]);

  const saveToHistory = useCallback((newData: Uint8Array) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(new Uint8Array(newData));
    if (newHistory.length > 50) newHistory.shift();
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const commitImportAdjustment = useCallback(() => {
    if (rawImport) setRawImport(null);
  }, [rawImport]);

  const undo = useCallback(() => {
    commitImportAdjustment();
    if (historyIndex > 0) {
      const prevData = history[historyIndex - 1];
      setPixelData(new Uint8Array(prevData));
      setHistoryIndex(historyIndex - 1);
    }
  }, [history, historyIndex, commitImportAdjustment]);

  const redo = useCallback(() => {
    commitImportAdjustment();
    if (historyIndex < history.length - 1) {
      const nextData = history[historyIndex + 1];
      setPixelData(new Uint8Array(nextData));
      setHistoryIndex(historyIndex + 1);
    }
  }, [history, historyIndex, commitImportAdjustment]);

  const clearCanvas = useCallback(() => {
    setRawImport(null);
    const cleared = new Uint8Array(WIDTH * HEIGHT).fill(0);
    setPixelData(cleared);
    saveToHistory(cleared);
    if (currentColorIdx === 0) {
      setCurrentColorIdx(3);
    }
  }, [saveToHistory, currentColorIdx, WIDTH, HEIGHT]);

  const commitPixels = useCallback((newData: Uint8Array) => {
    setRawImport(null);
    setPixelData(newData);
    saveToHistory(newData);
  }, [saveToHistory]);

  const setPixelsLive = useCallback((newData: Uint8Array) => {
    setPixelData(newData);
  }, []);

  const applyPermutation = useCallback((mapping: number[]) => {
    setRawImport(null);
    const newData = new Uint8Array(WIDTH * HEIGHT);
    for (let i = 0; i < pixelData.length; i++) {
      newData[i] = mapping[pixelData[i]];
    }
    setPixelData(newData);
    saveToHistory(newData);
    setIsPermutationMode(false);
  }, [pixelData, saveToHistory, WIDTH, HEIGHT]);

  const togglePalette = useCallback(() => {
    setPaletteIdx((prev) => (prev === 0 ? 1 : 0));
  }, []);

  const quantize = useCallback((imgData: ImageData, weights: number[]): Uint8Array => {
    const newData = new Uint8Array(WIDTH * HEIGHT);
    const weightMultipliers = weights.map(w => Math.pow(2, -w));
    const currentPaletteRGB = paletteRGB;
    
    for (let i = 0; i < imgData.data.length; i += 4) {
      const r = imgData.data[i];
      const g = imgData.data[i + 1];
      const b = imgData.data[i + 2];
      let minIdx = 0, minDistance = Infinity;
      for (let p = 0; p < currentPaletteRGB.length; p++) {
        const pr = currentPaletteRGB[p].r, pg = currentPaletteRGB[p].g, pb = currentPaletteRGB[p].b;
        const distSq = Math.pow(r - pr, 2) + Math.pow(g - pg, 2) + Math.pow(b - pb, 2);
        const adjustedDist = distSq * (weightMultipliers[p] || 1);
        if (adjustedDist < minDistance) { minDistance = adjustedDist; minIdx = p; }
      }
      newData[i / 4] = minIdx;
    }
    return newData;
  }, [paletteRGB, WIDTH, HEIGHT]);

  useEffect(() => {
    if (rawImport) {
      const newData = quantize(rawImport.imageData, importWeights);
      setPixelData(newData);
    }
  }, [rawImport, importWeights, quantize]);

  const handleImport = useCallback((file: File) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = WIDTH; canvas.height = HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = currentPalette[0];
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      const targetAspect = WIDTH / HEIGHT;
      const imgAspect = img.width / img.height;
      let dw, dh, dx, dy;
      if (imgAspect > targetAspect) { dw = WIDTH; dh = WIDTH / imgAspect; dx = 0; dy = (HEIGHT - dh) / 2; }
      else { dh = HEIGHT; dw = HEIGHT * imgAspect; dy = 0; dx = (WIDTH - dw) / 2; }
      ctx.drawImage(img, dx, dy, dw, dh);
      const imgData = ctx.getImageData(0, 0, WIDTH, HEIGHT);
      setImportWeights([0, 0, 0, 0]);
      const initialPixels = quantize(imgData, [0, 0, 0, 0]);
      setRawImport({ imageData: imgData, bgIndex: 0 });
      setPixelData(initialPixels);
      saveToHistory(initialPixels);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const hexRegex = /(?:0x|\$)?([0-9a-fA-F]{2})/g;
        const matches = [...text.matchAll(hexRegex)];
        
        const expectedByteCount = currentPMode === PMode.PMODE1 ? 3072 : 6144;
        const bytes = matches.map(m => parseInt(m[1], 16)).slice(0, expectedByteCount);
        
        while (bytes.length < expectedByteCount) {
          bytes.push(0);
        }
        
        const newData = new Uint8Array(WIDTH * HEIGHT);
        
        if (currentPMode === PMode.PMODE1) {
          const inverseMapping = paletteIdx === 0 ? [0, 2, 3, 1] : [0, 1, 2, 3];
          for (let i = 0; i < 3072; i++) {
            const byte = bytes[i];
            for (let p = 0; p < 4; p++) {
              const translatedPixel = (byte >> (6 - (p * 2))) & 0x03;
              const appPixel = inverseMapping[translatedPixel];
              const pixelIdx = i * 4 + p;
              if (pixelIdx < newData.length) newData[pixelIdx] = appPixel;
            }
          }
        } else {
          // PMODE4: 1 bit per pixel, 8 pixels per byte
          for (let i = 0; i < 6144; i++) {
            const byte = bytes[i];
            for (let p = 0; p < 8; p++) {
              const pixel = (byte >> (7 - p)) & 0x01;
              const pixelIdx = i * 8 + p;
              if (pixelIdx < newData.length) newData[pixelIdx] = pixel;
            }
          }
        }
        
        setRawImport(null);
        setPixelData(newData);
        saveToHistory(newData);
      };
      reader.readAsText(file);
    };

    img.src = url;
  }, [currentPalette, quantize, saveToHistory, paletteIdx, currentPMode, WIDTH, HEIGHT]);

  const handleWeightChange = (idx: number, val: number) => {
    const newWeights = [...importWeights];
    newWeights[idx] = val;
    setImportWeights(newWeights);
  };

  const handleWeightCommit = () => saveToHistory(new Uint8Array(pixelData));

  const handleExport = useCallback(() => {
    if (exportFormat === ExportFormat.PNG) {
      const canvas = document.createElement('canvas');
      canvas.width = WIDTH; canvas.height = HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const imageData = ctx.createImageData(WIDTH, HEIGHT);
      for (let i = 0; i < pixelData.length; i++) {
        const colorHex = currentPalette[pixelData[i]] || currentPalette[0] || '#000000';
        const r = parseInt(colorHex.slice(1, 3), 16), g = parseInt(colorHex.slice(3, 5), 16), b = parseInt(colorHex.slice(5, 7), 16);
        const idx = i * 4; imageData.data[idx] = r; imageData.data[idx + 1] = g; imageData.data[idx + 2] = b; imageData.data[idx + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
      const link = document.createElement('a'); link.download = `${currentPMode.toLowerCase()}_artwork.png`; link.href = canvas.toDataURL(); link.click();
    } else {
      // C or ASM export
      const bytes: number[] = [];
      
      if (currentPMode === PMode.PMODE1) {
        // Translation mapping for Motorola 6847 VDG
        // Palette 0 (Set 1): App [Green, Red, Yellow, Blue] -> 6847 [Green, Yellow, Blue, Red]
        // Palette 1 (Set 2): App [Buff, Cyan, Magenta, Orange] -> 6847 [Buff, Cyan, Magenta, Orange]
        const colorMapping = paletteIdx === 0 ? [0, 3, 1, 2] : [0, 1, 2, 3];

        for (let y = 0; y < HEIGHT; y++) {
          for (let x = 0; x < WIDTH; x += 4) {
            let byte = 0;
            for (let p = 0; p < 4; p++) {
              const pixel = pixelData[y * WIDTH + (x + p)];
              const translatedPixel = colorMapping[pixel & 0x03];
              byte |= translatedPixel << (6 - (p * 2));
            }
            bytes.push(byte);
          }
        }
      } else {
        // PMODE4: 1 bit per pixel, 8 pixels per byte
        for (let y = 0; y < HEIGHT; y++) {
          for (let x = 0; x < WIDTH; x += 8) {
            let byte = 0;
            for (let p = 0; p < 8; p++) {
              const pixel = pixelData[y * WIDTH + (x + p)];
              byte |= (pixel & 0x01) << (7 - p);
            }
            bytes.push(byte);
          }
        }
      }

      let content = '';
      if (exportFormat === ExportFormat.C) {
        content = bytes.map(b => `0x${b.toString(16).padStart(2, '0')},`).join('\n');
      } else {
        content = bytes.map(b => ` FCB $${b.toString(16).padStart(2, '0')}`).join('\n');
      }

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = exportFormat === ExportFormat.C ? `${currentPMode.toLowerCase()}_artwork.c` : `${currentPMode.toLowerCase()}_artwork.asm`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [pixelData, currentPalette, exportFormat, paletteIdx, currentPMode, WIDTH, HEIGHT]);

  const handleCopy = useCallback(async () => {
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH; canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const imageData = ctx.createImageData(WIDTH, HEIGHT);
    for (let i = 0; i < pixelData.length; i++) {
      const colorHex = currentPalette[pixelData[i]] || currentPalette[0] || '#000000';
      const r = parseInt(colorHex.slice(1, 3), 16), g = parseInt(colorHex.slice(3, 5), 16), b = parseInt(colorHex.slice(5, 7), 16);
      const idx = i * 4; imageData.data[idx] = r; imageData.data[idx + 1] = g; imageData.data[idx + 2] = b; imageData.data[idx + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
        } catch (err) {
          console.error('Failed to copy: ', err);
        }
      }
    }, 'image/png');
  }, [pixelData, currentPalette, WIDTH, HEIGHT]);

  const handlePaste = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find(type => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], "pasted_image.png", { type: imageType });
          handleImport(file);
          return;
        }
        const textType = item.types.find(type => type === 'text/plain');
        if (textType) {
          const blob = await item.getType(textType);
          const file = new File([blob], "pasted_text.txt", { type: textType });
          handleImport(file);
          return;
        }
      }
    } catch (err) {
      console.error('Failed to paste: ', err);
    }
  }, [handleImport]);

  return (
    <div className="flex flex-col h-full bg-[#1e1e1e] select-none">
      <Header 
        undo={undo} redo={redo} canUndo={historyIndex > 0} canRedo={historyIndex < history.length - 1}
        download={handleExport} clear={clearCanvas} onCopy={handleCopy} onPaste={handlePaste} onImport={handleImport}
        importWeights={rawImport ? importWeights : null} onWeightChange={handleWeightChange} onWeightCommit={handleWeightCommit}
        palette={currentPalette} exportFormat={exportFormat} setExportFormat={setExportFormat}
        currentPMode={currentPMode} setPMode={setCurrentPMode}
      />
      <main className="flex flex-1 overflow-hidden">
        <Sidebar 
          currentTool={currentTool} setTool={setCurrentTool} currentColorIdx={currentColorIdx} setColorIdx={setCurrentColorIdx}
          isPermuteActive={isPermutationMode} onTogglePermute={() => setIsPermutationMode(!isPermutationMode)} onTogglePalette={togglePalette}
          paletteIdx={paletteIdx} palette={currentPalette} fontSizeIdx={fontSizeIdx} setFontSizeIdx={setFontSizeIdx}
          currentPMode={currentPMode}
        />
        <div className="flex-1 flex items-center justify-center bg-[#333] p-8 overflow-auto custom-scrollbar">
          {isPermutationMode ? (
            <PermutationView pixelData={pixelData} onSelect={applyPermutation} onCancel={() => setIsPermutationMode(false)} palette={currentPalette} width={WIDTH} height={HEIGHT} />
          ) : (
            <CanvasEditor 
              pixelData={pixelData} commitPixels={commitPixels} setPixelsLive={setPixelsLive} currentTool={currentTool} currentColorIdx={currentColorIdx}
              width={WIDTH} height={HEIGHT} zoom={ZOOM} palette={currentPalette} fontSizeIdx={fontSizeIdx}
              scrap={scrap} setScrap={setScrap}
            />
          )}
        </div>
      </main>
      <footer className="h-8 bg-[#000] border-t-2 border-[#444] px-4 flex items-center justify-between text-xs text-gray-400">
        <div>{WIDTH} x {HEIGHT} | {currentPMode} | ZOOM: {ZOOM}x | PALETTE: {currentPMode === PMode.PMODE1 ? (paletteIdx === 0 ? 'GRYB' : 'BCMO') : (paletteIdx === 0 ? 'BKGR' : 'BKBF')}</div>
        <div>{isPermutationMode ? 'PERMUTATION MODE' : `${currentTool.toUpperCase()} | COLOR: ${currentColorIdx}`}</div>
      </footer>
    </div>
  );
};

export default App;
